import { useTranslation } from 'react-i18next';
import type { Schedule } from '@ghostscreen/contracts';
import { formatDateTime } from '../../i18n';
import { daypartColorFor } from '../../lib/schedule/daypartPalette';
import { formatLocalTime } from '../../lib/schedule/formatLocalWindow';
import {
  appliesOnDay,
  formatWeekRange,
  jsDayToContractDay,
  timelineSegmentsForDay,
  weekDaysContaining,
} from '../../lib/schedule/scheduleWindow';

const HOUR_LABELS = ['00:00', '03:00', '06:00', '09:00', '12:00', '15:00', '18:00', '21:00'] as const;

type ScheduleWeekTimelineProps = {
  schedules: Schedule[];
  now?: Date;
  onOpenSchedule?: (scheduleId: string) => void;
};

/**
 * “This week” calendar — Ghost Screen Schedule.dc.html.
 */
export function ScheduleWeekTimeline({
  schedules,
  now = new Date(),
  onOpenSchedule,
}: ScheduleWeekTimelineProps) {
  const { t } = useTranslation('schedule');
  const days = weekDaysContaining(now);
  const todayKey = dateKey(now);
  const legend = uniqueLegend(schedules, t('untitled'));

  return (
    <section
      className="overflow-hidden rounded-[14px] border"
      style={{
        background: 'var(--gs-panel)',
        borderColor: 'var(--gs-line)',
        animation: 'gs-in 260ms ease both',
      }}
    >
      <div
        className="flex flex-wrap items-center gap-3 border-b px-[22px] py-[17px]"
        style={{ borderColor: 'var(--gs-line)' }}
      >
        <span className="text-base font-bold" style={{ color: 'var(--gs-ink)' }}>
          {t('timeline.title')}
        </span>
        <span className="text-[13px]" style={{ color: 'var(--gs-muted)' }}>
          {t('timeline.subtitle', { range: formatWeekRange(days) })}
        </span>
        {legend.length > 0 ? (
          <span className="ms-auto flex flex-wrap items-center gap-3.5">
            {legend.map((item) => (
              <span
                key={item.id}
                className="flex items-center gap-[7px] text-[12.5px]"
                style={{ color: 'var(--gs-ink2)' }}
              >
                <span
                  className="h-[11px] w-[11px] rounded-[3px]"
                  style={{ background: item.color }}
                  aria-hidden
                />
                {item.label}
              </span>
            ))}
          </span>
        ) : null}
      </div>

      <div className="overflow-x-auto px-[22px] pb-[22px] pt-[18px]">
        <div className="flex min-w-[720px] flex-col gap-1.5">
          <div className="grid grid-cols-[74px_1fr] items-center gap-3">
            <span />
            <span className="grid grid-cols-8">
              {HOUR_LABELS.map((label) => (
                <span
                  key={label}
                  className="text-[11px] font-semibold tracking-[0.08em]"
                  style={{ color: 'var(--gs-muted)' }}
                >
                  {formatLocalTime(label)}
                </span>
              ))}
            </span>
          </div>

          {days.map((day) => {
            const contractDay = jsDayToContractDay(day);
            const isToday = dateKey(day) === todayKey;
            const weekday = formatDateTime(day, { weekday: 'short' });
            const dateLabel = formatDateTime(day, { month: 'short', day: 'numeric' });

            return (
              <div
                key={day.toISOString()}
                className="grid grid-cols-[74px_1fr] items-center gap-3"
              >
                <span className="flex flex-col gap-px">
                  <span
                    className="text-[13px] font-bold"
                    style={{ color: isToday ? 'var(--gs-accent)' : 'var(--gs-ink)' }}
                  >
                    {weekday}
                  </span>
                  <span className="text-[11px]" style={{ color: 'var(--gs-muted)' }}>
                    {dateLabel}
                  </span>
                </span>

                <span
                  className="relative block h-[38px] overflow-hidden rounded-lg border"
                  style={{
                    background: 'var(--gs-panel2)',
                    borderColor: 'var(--gs-line)',
                  }}
                >
                  {schedules.flatMap((schedule) => {
                    if (!appliesOnDay(schedule.daysOfWeek, contractDay)) return [];
                    const color = daypartColorFor(schedule.id);
                    const name = schedule.name?.trim() || t('timeline.daypartFallback');
                    return timelineSegmentsForDay(
                      schedule.startLocalTime,
                      schedule.endLocalTime,
                      name,
                    ).map((segment, index) => (
                      <button
                        key={`${schedule.id}-${index}`}
                        type="button"
                        title={t('timeline.segmentTitle', {
                          name,
                          start: formatLocalTime(schedule.startLocalTime),
                          end: formatLocalTime(schedule.endLocalTime),
                        })}
                        onClick={() => onOpenSchedule?.(schedule.id)}
                        className="absolute bottom-1 top-1 flex items-center overflow-hidden rounded-md px-[9px] text-start"
                        style={{
                          insetInlineStart: `${segment.startPct}%`,
                          width: `${Math.max(segment.widthPct, 1.2)}%`,
                          background: color.bar,
                        }}
                      >
                        <span className="truncate text-[11.5px] font-bold text-white">
                          {segment.label}
                        </span>
                      </button>
                    ));
                  })}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function dateKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function uniqueLegend(schedules: Schedule[], untitled: string) {
  const seen = new Set<string>();
  const items: { id: string; label: string; color: string }[] = [];
  for (const schedule of schedules) {
    const label = schedule.name?.trim() || untitled;
    if (seen.has(label)) continue;
    seen.add(label);
    items.push({ id: schedule.id, label, color: daypartColorFor(schedule.id).bar });
    if (items.length >= 5) break;
  }
  return items;
}
